import React, { useState } from 'react';

const AirIQSupplierHighlight = () => {
  const [activeTab, setActiveTab] = useState('about');
  const [billingCycle, setBillingCycle] = useState(1); // 1 = Monthly, 2 = Annually

  // Company Information - Zotrai (YOUR COMPANY)
  const companyInfo = {
    name: "Zotrai",
    themeColor: "#FF7F00",
    description: "Your trusted B2B travel technology partner",
    founded: "2020",
    headquarters: "India",
    agents: "10,000+",
    suppliers: "50+",
    countries: "100+",
    uptime: "99.9%",
    apiCalls: "100M+",
    team: "100+",
    email: "support@zotrai.com",
    phone: "+91-7207642485"
  };

 // Why Suppliers Choose Zotrai - REAL DATA
  const whyZotraiData = {
    benefits: [
        {
        id: 1,
        title: "Single API Integration, Multiple Connections",
        description: "Connect once with Zotrai and get instant access to all our partner suppliers - no multiple integrations needed",
        icon: "ri-plug-line",
        stat: "1",
        statLabel: "Integration"
      },
     {
  id: 2,
  title: "Launch B2B, B2C & Corporate Portals",
  description: "Fully customizable multi-agent, direct-sell, and corporate booking platforms under your brand",
  icon: "ri-window-line",
  stat: "All-in-One",
  statLabel: "Portal Suite"
},
    
      {
  id: 3,
  title: "Save 95% on Integration Costs",
  description: "No setup fees, no integration charges.",
icon: "ri-coins-line",

  stat: "95%",
  statLabel: "Cost Savings"
},
      {
        id: 4,
        title: "Real-time Analytics Dashboard",
        description: "Track your inventory performance, booking patterns, and agent behavior with detailed analytics",
        icon: "ri-bar-chart-2-line",
        stat: "24/7",
        statLabel: "Monitoring"
      },
      {
        id: 5,
        title: "99.9% Uptime Guarantee",
        description: "Enterprise-grade infrastructure with redundant systems ensuring your APIs are always available",
        icon: "ri-shield-check-line",
        stat: "99.9%",
        statLabel: "Uptime"
      },
      {
        id: 6,
        title: "Dedicated Account Manager",
        description: "Every supplier gets a dedicated technical account manager for seamless integration and support",
        icon: "ri-customer-service-line",
        stat: "24/7",
        statLabel: "Support"
      }
    ],
    process: [
      {
        step: 1,
        title: "Add Your API Credentials",
        description: "Just Login, paste your API keys, and we handle the rest – zero development effort"
      },
       {
        step: 2,
        title: "Select & Customize Template",
        description: "Pick from our library of 100+ responsive templates and match your brand identity"
      },
      {
        step: 3,
        title: "Go Live Instantly",
        description: "Deploy to production in one click and start accepting real bookings"
      }
    ]
  };

  // SEO-friendly structured data for Air iQ
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Air iQ",
    "description": "Leading B2B travel distribution platform in India offering air inventory and hotel distribution solutions",
    "url": "https://www.airiqonline.in",
    "areaServed": "India, Nepal, Bangladesh, Middle East, Southeast Asia",
    "serviceType": ["Flight Booking API", "Hotel Distribution Platform", "Air Series Fares", "B2B Travel Portal"],
    "foundingDate": "2018",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "New Delhi",
      "addressCountry": "India"
    }
  };

  // Air iQ specific data based on search results [citation:1][citation:4]
  const airiqData = {
    name: "Air iQ",
    description: "One of India's leading B2B travel distribution platforms, pioneering air series fares and tech-led travel solutions for agents across India and beyond.",
    longDescription: "Air iQ Online has established itself as a trusted name among 65,000+ travel professionals across India, Nepal, and Bangladesh. Founded with a vision to democratize travel distribution, the company first built a stronghold in the air distribution segment with innovative access to over 2,000 airlines and 4,000+ API clients. After establishing dominance in air inventory, Air iQ has now launched their highly anticipated Hotel Distribution Platform, marking a significant milestone in their journey to become a full-spectrum travel solutions provider. The platform offers real-time access to 450,000+ hotels and resorts across India, the Middle East, Southeast Asia, and Europe. Air iQ is known for pioneering air series fares and delivering next-gen technology solutions that simplify travel bookings for their trade partners. Their commitment to innovation, transparency, and reliability has made them the preferred choice for travel agents seeking comprehensive travel products through a single, powerful platform. [citation:1][citation:4]",
    founded: "2018",
    headquarters: "New Delhi, India",
    employees: "150+",
    countries: "15+",
    travelPartners: "65,000+",
    apiClients: "4,000+",
    airlines: "2,000+",
    hotels: "450,000+",
    hotelRegions: ["India", "Middle East", "Southeast Asia", "Europe"],
    themeColor: "#0284C7", // Sky Blue color
    website: "https://www.airiqonline.in",
    apiType: "RESTful API",
    integrationType: "Direct API Integration",
    support: "24/7 Dedicated Support",
    platforms: ["airiq.in", "airiqonline.in"],
    leadership: {
      ceo: "Param Sidhu",
      managingDirectors: ["Nishi Kant Aggarwal", "Sashi Kant Agarwal"],
      executiveDirector: "Ajay Kumar Wadhawan"
    }
  };

  // Key features of Air iQ
  const features = [
    {
      icon: "✈️",
      title: "2,000+ Airlines",
      description: "Access to extensive air inventory including full-service carriers and low-cost airlines"
    },
    {
      icon: "🏨",
      title: "450,000+ Hotels",
      description: "Real-time access to hotels and resorts across India, Middle East, Southeast Asia, and Europe"
    },
    {
      icon: "🤝",
      title: "65,000+ Travel Partners",
      description: "Trusted by travel professionals across India, Nepal, and Bangladesh"
    },
    {
      icon: "🚀",
      title: "Air Series Fares",
      description: "Pioneering innovative air series fares for competitive advantage"
    }
  ];

  // Services offered by Air iQ based on search results [citation:1][citation:4]
  const services = [
    {
      id: 1,
      title: "Flight Booking API",
      description: "Access to 2,000+ airlines with real-time availability, competitive pricing, and instant confirmation through powerful API integration.",
      features: [
        "2,000+ airlines inventory",
        "Air series fares",
        "Real-time availability",
        "Instant confirmation",
        "PNR generation",
        "Multi-GDS connectivity"
      ],
      icon: "✈️"
    },
    {
      id: 2,
      title: "Hotel Distribution Platform",
      description: "Newly launched hotel platform offering 450,000+ properties across India, Middle East, Southeast Asia, and Europe with real-time access.",
      features: [
        "450,000+ hotels & resorts",
        "Real-time availability",
        "Dynamic pricing",
        "Rich content & images",
        "Instant confirmation",
        "Multi-currency support"
      ],
      icon: "🏨"
    },
    {
      id: 3,
      title: "B2B Travel Portal",
      description: "Comprehensive trade-friendly platform (airiqonline.in) designed for leisure tour operators and travel service providers.",
      features: [
        "User-friendly interface",
        "FIT portal functionality",
        "Fixed departure options",
        "Advanced search filters",
        "Booking management",
        "Reporting & analytics"
      ],
      icon: "💻"
    },
    {
      id: 4,
      title: "Air Series Fares",
      description: "Pioneering innovative air series fares that provide competitive advantage and exclusive pricing for trade partners.",
      features: [
        "Exclusive fare programs",
        "Volume-based pricing",
        "Seasonal series",
        "Corporate programs",
        "Group booking options",
        "Special discounts"
      ],
      icon: "📊"
    },
    {
      id: 5,
      title: "Fixed Departure Platform",
      description: "Specialized platform (airiq.in) for fixed departure tours and pre-packaged travel solutions.",
      features: [
        "Pre-packaged tours",
        "Fixed itineraries",
        "Group departures",
        "Seasonal packages",
        "Customizable options",
        "Instant quoting"
      ],
      icon: "🗓️"
    },
    {
      id: 6,
      title: "API Integration Services",
      description: "Comprehensive API solutions with 4,000+ API clients benefiting from seamless integration capabilities.",
      features: [
        "RESTful APIs",
        "Comprehensive documentation",
        "Sandbox environment",
        "24/7 technical support",
        "Scalable architecture",
        "Real-time data sync"
      ],
      icon: "⚙️"
    }
  ];

  // Technology highlights based on search results [citation:1]
  const technology = [
    {
      title: "RESTful APIs",
      description: "Modern RESTful API architecture with comprehensive documentation for seamless integration"
    },
    {
      title: "AI-Native Mapping",
      description: "Integration with Vervotech's AI-native accommodation mapping technology for accurate hotel data [citation:1]"
    },
    {
      title: "Real-time Availability",
      description: "Live inventory updates from airlines and hotels with instant confirmation"
    },
    {
      title: "Multi-currency Support",
      description: "Transactions in multiple currencies with dynamic conversion"
    },
    {
      title: "Scalable Architecture",
      description: "Enterprise-grade infrastructure handling millions of API calls"
    },
    {
      title: "Data Accuracy",
      description: "99.999% mapping accuracy for hotel data eliminating booking errors [citation:1]"
    }
  ];

  // Integration process based on search results [citation:2][citation:5]
  const integrationProcess = [
    {
      step: 1,
      title: "API Documentation",
      description: "Receive comprehensive API docs and credentials for Air iQ platform"
    },
    {
      step: 2,
      title: "Sandbox Testing",
      description: "Test integration in Air iQ's staging environment"
    },
    {
      step: 3,
      title: "Go Live",
      description: "Deploy to production with 24/7 dedicated support"
    }
  ];

  // Why choose Air iQ
  const whyChoose = [
    "65,000+ trusted travel partners across India, Nepal, and Bangladesh [citation:1]",
    "4,000+ API clients benefiting from seamless integration",
    "2,000+ airlines with innovative air series fares",
    "450,000+ hotels with AI-native mapping technology [citation:1]",
    "Pioneering air series fares for competitive advantage",
    "Comprehensive B2B portals: airiq.in and airiqonline.in [citation:4]"
  ];

  // Supplier logos array with navigation paths
  const suppliers = [
    { 
      id: 1, 
      name: "Tripjack", 
      logo: "/assets/images/suppliers/tripjack.png",
      path: "/supplier/trip-jack"
    },
    { 
      id: 2, 
      name: "TBO", 
      logo: "/assets/images/suppliers/tbo.png",
      path: "/supplier/tbo"
    },
    { 
      id: 3, 
      name: "Akbar Travels", 
      logo: "/assets/images/suppliers/akbar.png",
      path: "/supplier/akbar-travels"
    },
    { 
      id: 4, 
      name: "RezLive", 
      logo: "/assets/images/suppliers/rezlive.png",
      path: "/supplier/rezlive"
    },
    { 
      id: 5, 
      name: "GRN Connect", 
      logo: "/assets/images/suppliers/grn.png",
      path: "/supplier/grn-connect"
    },
    { 
      id: 6, 
      name: "Riya Travel", 
      logo: "/assets/images/suppliers/riya.png",
      path: "/supplier/riya-travel"
    },
    { 
      id: 7, 
      name: "Viator", 
      logo: "/assets/images/suppliers/viator.png",
      path: "/supplier/viator"
    },
    { 
      id: 8, 
      name: "Air iQ", 
      logo: "/assets/images/suppliers/airiq.png",
      path: "/supplier/air-iq"
    }
  ];

  // Pricing plans data
  const plans = [
    {
      id: 1,
      name: "Starter",
      description: "🚀 All-in-one powerful plan with complete access to every feature.",
      monthly: 2999,
      annually: 29999,
      popular: true,
      emoji: "🚀",
      features: [
        "Search, compare, and book flights from top global suppliers in one place",
        "Access and connect with 1,000+ verified global DMCs",
        "Smart accounting suite with ledgers, invoices, supplier settlements, and corporate payments",
        "Capture leads directly from social media and run promotions from a single dashboard",
        "Generate professional itineraries in minutes using AI",
        "AI-powered visa processing and document management system",
        "One unified platform for Flights, Hotels, Visas, Cars, Sightseeing, and more",
        "Integrated and secure payment gateway",
        "Plug in and manage your own supplier APIs seamlessly",
        "AI-based WhatsApp automation for leads, follow-ups, and customer support",
        "Unlimited email marketing and promotional campaigns",
        "Advanced reports and analytics for sales, revenue, and performance tracking",
      ],
      addons: [
        "Launch your own B2C booking website for customers in minutes – just INR 999",
        "Launch a full-featured B2C travel portal with flights, hotels, and packages using your preferred suppliers – INR 1,999",
        "Launch a B2B travel portal for your agent network with flights, hotels, and packages – INR 1,999",
        "Launch a B2E corporate travel portal for your business clients with flights, hotels, and packages – INR 1,999",
      ],
    },
  ];

  const getAccentColor = (planName) => {
    if (planName === "Starter") return "#10B981";
    if (planName === "Pro") return "#3B82F6";
    return "#8B5CF6";
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${companyInfo.email}`;
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${companyInfo.phone.replace(/[^0-9+]/g, '')}`;
  };

  const handleSupplierClick = (path) => {
    window.location.href = path;
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Air iQ - Leading B2B Travel Distribution Platform | Zotrai</title>
        <meta name="description" content="Air iQ is one of India's leading B2B travel distribution platforms with 2,000+ airlines, 450,000+ hotels, and 65,000+ trusted travel partners." />
        <meta name="keywords" content="Air iQ, B2B travel platform, flight booking API, hotel distribution, air series fares, travel technology India" />
        <link rel="canonical" href="https://yourcompany.com/supplier/air-iq" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Air iQ - Leading B2B Travel Distribution Platform" />
        <meta property="og:description" content="Connect with Air iQ's extensive network of 2,000+ airlines and 450,000+ hotels through Zotrai's single API integration." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Main Content with airiq- prefix */}
      <div className="airiq-supplier-container">
        {/* Header Section - Air iQ Hero */}
        <header className="airiq-header">
          <div className="airiq-container">
            <h1 className="airiq-title">
              <span className="airiq-highlight" style={{ color: airiqData.themeColor }}>Air iQ</span>
              <br />
              India's Leading B2B Travel Distribution Platform
            </h1>
            <p className="airiq-subtitle">
              {airiqData.description}
            </p>
          </div>
        </header>

        {/* Tabs Navigation - Same as others */}
        <div className="airiq-tabs-container">
          <div className="airiq-container">
            <div className="airiq-tabs">
              <button 
                className={`airiq-tab ${activeTab === 'about' ? 'airiq-tab-active' : ''}`}
                onClick={() => setActiveTab('about')}
              >
                About
              </button>
              <button 
                className={`airiq-tab ${activeTab === 'services' ? 'airiq-tab-active' : ''}`}
                onClick={() => setActiveTab('services')}
              >
                Services
              </button>
              <button 
                className={`airiq-tab ${activeTab === 'technology' ? 'airiq-tab-active' : ''}`}
                onClick={() => setActiveTab('technology')}
              >
                Technology
              </button>
              <button 
                className={`airiq-tab ${activeTab === 'whyzotrai' ? 'airiq-tab-active' : ''}`}
                onClick={() => setActiveTab('whyzotrai')}
              >
                Why Zotrai <span className="airiq-tab-badge">For Suppliers</span>
              </button>
              <button 
                className={`airiq-tab ${activeTab === 'suppliers' ? 'airiq-tab-active' : ''}`}
                onClick={() => setActiveTab('suppliers')}
              >
                Suppliers
              </button>
              <button 
                className={`airiq-tab ${activeTab === 'pricing' ? 'airiq-tab-active' : ''}`}
                onClick={() => setActiveTab('pricing')}
              >
                Pricing
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="airiq-tab-content">
          {activeTab === 'about' && (
            <section className="airiq-about">
              <div className="airiq-container">
                <div className="airiq-grid">
                  <div className="airiq-about-content">
                    <h2 className="airiq-section-title">About <span className="airiq-accent" style={{ color: airiqData.themeColor }}>Air iQ</span></h2>
                    <p className="airiq-text">
                      {airiqData.longDescription}
                    </p>
                    
                    <div className="airiq-features">
                      {features.map((feature, index) => (
                        <div key={index} className="airiq-feature">
                          <span className="airiq-feature-icon">{feature.icon}</span>
                          <div>
                            <h4>{feature.title}</h4>
                            <p>{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="airiq-card">
                    <h3 className="airiq-card-title">Air iQ at a Glance</h3>
                    <ul className="airiq-list">
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        65,000+ Trusted Travel Partners [citation:1]
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        4,000+ API Clients
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        2,000+ Airlines
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        450,000+ Hotels & Resorts [citation:1]
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        Coverage: India, Middle East, Southeast Asia, Europe
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        Pioneering Air Series Fares
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        AI-Native Hotel Mapping Technology [citation:1]
                      </li>
                      <li className="airiq-list-item">
                        <span className="airiq-check" style={{ color: airiqData.themeColor }}>✓</span>
                        B2B Portals: airiq.in & airiqonline.in [citation:4]
                      </li>
                    </ul>

                 
                   
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'services' && (
            <section className="airiq-services">
              <div className="airiq-container">
                <h2 className="airiq-section-title airiq-text-center">
                  Comprehensive Travel Solutions
                </h2>
                <p className="airiq-section-subtitle">
                  Through our integration with Air iQ, we deliver end-to-end travel technology solutions for agents across India and beyond
                </p>

                <div className="airiq-services-grid">
                  {services.map((service) => (
                    <div key={service.id} className="airiq-service-card">
                      <div className="airiq-service-icon">{service.icon}</div>
                      <h3 className="airiq-service-title">{service.title}</h3>
                      <p className="airiq-service-description">{service.description}</p>
                      <ul className="airiq-service-list">
                        {service.features.map((feature, index) => (
                          <li key={index} className="airiq-service-list-item">
                            <span className="airiq-dot" style={{ color: airiqData.themeColor }}>•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'technology' && (
            <section className="airiq-technology">
              <div className="airiq-container">
                <div className="airiq-grid">
                  <div className="airiq-integration-box">
                    <h3 className="airiq-card-title">Seamless Integration</h3>
                    <p className="airiq-text">
                      Our integration with Air iQ's robust APIs ensures smooth data flow, real-time availability, and reliable performance for flight and hotel bookings. Air iQ leverages AI-native mapping technology for unparalleled data accuracy. [citation:1]
                    </p>
                    <div className="airiq-tags">
                      {technology.map((tech, index) => (
                        <span key={index} className="airiq-tag">{tech.title}</span>
                      ))}
                    </div>
                  </div>

                  <div className="airiq-benefits-box">
                    <h3 className="airiq-card-title">Key Advantages</h3>
                    <ul className="airiq-benefits-list">
                      <li className="airiq-benefits-item">
                        <span className="airiq-benefits-icon">⚡</span>
                        Lightning-fast API response times
                      </li>
                      <li className="airiq-benefits-item">
                        <span className="airiq-benefits-icon">🛡️</span>
                        Enterprise-grade security protocols
                      </li>
                      <li className="airiq-benefits-item">
                        <span className="airiq-benefits-icon">🎧</span>
                        24/7 dedicated technical support
                      </li>
                      <li className="airiq-benefits-item">
                        <span className="airiq-benefits-icon">👥</span>
                        Experienced integration team
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'whyzotrai' && (
            <section className="airiq-why-zotrai-section">
              <div className="airiq-container">
                {/* Header */}
                <div className="airiq-why-header">
                  <span className="airiq-why-badge" style={{ background: `${companyInfo.themeColor}15`, color: companyInfo.themeColor }}>
                    <i className="ri-question-line"></i> WHY SUPPLIERS CHOOSE ZOTRAI
                  </span>
                  <h2 className="airiq-section-title">
                    Your Growth Partner in <span className="airiq-accent" style={{ color: companyInfo.themeColor }}>Travel Distribution</span>
                  </h2>
                  <p className="airiq-section-subtitle">
                    Join 50+ leading travel suppliers who trust us to connect them with travel agents
                  </p>
                </div>

                {/* Benefits Grid */}
                <div className="airiq-benefits-grid-full">
                  {whyZotraiData.benefits.map((benefit) => (
                    <div key={benefit.id} className="airiq-benefit-card">
                      <div className="airiq-benefit-icon-wrapper" style={{ background: `${companyInfo.themeColor}15` }}>
                        <i className={benefit.icon} style={{ color: companyInfo.themeColor }}></i>
                      </div>
                      <div className="airiq-benefit-content">
                        <h3>{benefit.title}</h3>
                        <p>{benefit.description}</p>
                        <div className="airiq-benefit-stat">
                          <span className="airiq-stat-highlight" style={{ color: companyInfo.themeColor }}>{benefit.stat}</span>
                          <span className="airiq-stat-label">{benefit.statLabel}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Integration Process */}
                <div className="airiq-integration-process">
                  <h3 className="airiq-process-title">Simple 3-Step Integration</h3>
                  <div className="airiq-process-steps">
                    {whyZotraiData.process.map((step) => (
                      <div key={step.step} className="airiq-process-step">
                        <div className="airiq-step-number" style={{ background: companyInfo.themeColor }}>{step.step}</div>
                        <h4>{step.title}</h4>
                        <p>{step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Section */}
                <div className="airiq-contact-section">
                  <h3>Ready to Join Our Supplier Network?</h3>
                  <p>Connect with agents through a single integration</p>
                  
                  <div className="airiq-contact-info">
                    <div className="airiq-contact-item" onClick={handleEmailClick}>
                      <div className="airiq-contact-icon" style={{ backgroundColor: `${companyInfo.themeColor}15` }}>
                        <i className="ri-mail-line" style={{ color: companyInfo.themeColor }}></i>
                      </div>
                      <div className="airiq-contact-details">
                        <span className="airiq-contact-label">Email Support</span>
                        <span className="airiq-contact-value">{companyInfo.email}</span>
                      </div>
                    </div>

                    <div className="airiq-contact-item" onClick={handlePhoneClick}>
                      <div className="airiq-contact-icon" style={{ backgroundColor: `${companyInfo.themeColor}15` }}>
                        <i className="ri-phone-line" style={{ color: companyInfo.themeColor }}></i>
                      </div>
                      <div className="airiq-contact-details">
                        <span className="airiq-contact-label">Phone Support</span>
                        <span className="airiq-contact-value">{companyInfo.phone}</span>
                      </div>
                    </div>
                  </div>

                  <p className="airiq-contact-note">
                    Our support team is available 24/7 to assist you with any questions
                  </p>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'suppliers' && (
            <section className="airiq-suppliers-section">
              <div className="airiq-container">
                {/* Section Header */}
                <div className="airiq-suppliers-header">
                  <span className="airiq-suppliers-badge">
                    <i className="ri-handshake-line"></i> OUR INTEGRATED SUPPLIERS
                  </span>
                  <h2 className="airiq-section-title">
                    Connect With <span className="airiq-accent">Leading Travel Suppliers</span>
                  </h2>
                  <p className="airiq-section-subtitle">
                    Direct integrations with industry-leading travel suppliers worldwide
                  </p>
                </div>

                {/* Clickable Logo Grid */}
                <div className="airiq-logo-grid">
                  {suppliers.map((supplier) => (
                    <div 
                      key={supplier.id} 
                      className="airiq-logo-item airiq-clickable"
                      onClick={() => handleSupplierClick(supplier.path)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSupplierClick(supplier.path);
                        }
                      }}
                    >
                      <div className="airiq-logo-wrapper">
                        <img 
                          src={supplier.logo} 
                          alt={supplier.name}
                          className="airiq-supplier-logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://via.placeholder.com/80x40?text=${supplier.name.charAt(0)}`;
                          }}
                        />
                      </div>
                      <span className="airiq-logo-name">{supplier.name}</span>
                      <span className="airiq-view-details-hint">
                        View Details <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  ))}
                </div>

                {/* Simple Stats */}
                <div className="airiq-supplier-stats-simple">
                 
                  <div className="airiq-stat-simple">
                    <span className="airiq-stat-number">1M+</span>
                    <span className="airiq-stat-label">Hotel Properties</span>
                  </div>
                  <div className="airiq-stat-simple">
                    <span className="airiq-stat-number">80+</span>
                    <span className="airiq-stat-label">Airlines</span>
                  </div>
                  <div className="airiq-stat-simple">
                    <span className="airiq-stat-number">300K+</span>
                    <span className="airiq-stat-label">Activities</span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'pricing' && (
            <section className="airiq-pricing-section">
              <div className="airiq-container">
                <div className="airiq-pricing-header">
                  <h2 className="airiq-section-title airiq-text-center">
                    Plans & <span className="airiq-accent">Pricing</span>
                  </h2>
                  <p className="airiq-section-subtitle">
                    Pick the plan that's right for your business growth
                  </p>
                  
                  {/* Billing Toggle */}
                  <div className="airiq-billing-toggle">
                    <button
                      className={`airiq-toggle-btn ${billingCycle === 1 ? 'airiq-toggle-active' : ''}`}
                      onClick={() => setBillingCycle(1)}
                    >
                      Monthly
                    </button>
                    <button
                      className={`airiq-toggle-btn ${billingCycle === 2 ? 'airiq-toggle-active' : ''}`}
                      onClick={() => setBillingCycle(2)}
                    >
                      Annually
                      <span className="airiq-save-badge">Save 20%</span>
                    </button>
                  </div>
                </div>

                {/* Pricing Cards */}
                <div className="airiq-pricing-grid">
                  {plans.map((plan) => {
                    const isMonthly = billingCycle === 1;
                    const accentColor = getAccentColor(plan.name);
                    
                    return (
                      <div key={plan.id} className="airiq-pricing-card-enhanced">
                        {plan.popular && (
                          <div className="airiq-popular-badge">
                            ⭐ MOST POPULAR
                          </div>
                        )}

                        <div className="airiq-pricing-card-content">
                          {/* Pricing Side */}
                          <div className="airiq-pricing-left" style={{ backgroundColor: plan.popular ? '#F4F9FF' : '#ffffff' }}>
                            <div className="airiq-pricing-emoji">{plan.emoji}</div>
                            <h3 className="airiq-pricing-name">{plan.name}</h3>
                            <p className="airiq-pricing-description">{plan.description}</p>

                            <div className="airiq-pricing-amount">
                              <span className="airiq-currency">INR</span>
                              <span className="airiq-price">
                                {isMonthly ? plan.monthly.toLocaleString() : plan.annually.toLocaleString()}
                              </span>
                              <span className="airiq-pricing-period">
                                /{isMonthly ? 'month' : 'year'}
                              </span>
                            </div>

                            <button 
                              className="airiq-pricing-cta"
                              style={{ backgroundColor: accentColor }}
                              onClick={() => window.location.href = '/signup'}
                            >
                              Start Free Trial <i className="ri-arrow-right-line"></i>
                            </button>

                            {/* Quick 3-Step Setup */}
                            <div className="airiq-quick-setup">
                              <h4 className="airiq-setup-title">Quick 3-Step Setup</h4>
                              <div className="airiq-setup-steps">
                                <div className="airiq-setup-step">
                                  <div className="airiq-step-marker" style={{ background: accentColor }}>1</div>
                                  <div className="airiq-step-content">
                                    <strong>Sign Up & Choose Plan</strong>
                                    <span>Create your account in 30 seconds - no credit card required</span>
                                  </div>
                                </div>
                                <div className="airiq-setup-step">
                                  <div className="airiq-step-marker" style={{ background: accentColor }}>2</div>
                                  <div className="airiq-step-content">
                                    <strong>Select Your Suppliers</strong>
                                    <span>Choose from leading travel suppliers with one click</span>
                                  </div>
                                </div>
                                <div className="airiq-setup-step">
                                  <div className="airiq-step-marker" style={{ background: accentColor }}>3</div>
                                  <div className="airiq-step-content">
                                    <strong>Launch Your Portal</strong>
                                    <span>Go live with your branded website in minutes</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Features Side */}
                          <div className="airiq-pricing-right">
                            <h5 className="airiq-features-title" style={{ color: accentColor }}>
                              Included in {plan.name}
                            </h5>
                            
                            <div className="airiq-features-list">
                              {plan.features.map((feature, index) => (
                                <div key={index} className="airiq-feature-item">
                                  <div className="airiq-feature-check" style={{ backgroundColor: `${accentColor}15` }}>
                                    <i className="ri-check-line" style={{ color: accentColor }}></i>
                                  </div>
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>

                            <h5 className="airiq-features-title airiq-addons-title" style={{ color: accentColor }}>
                              Add-Ons
                            </h5>
                            
                            <div className="airiq-addons-list">
                              {plan.addons.map((addon, index) => (
                                <div key={index} className="airiq-addon-item">
                                  <div className="airiq-addon-check" style={{ backgroundColor: `${accentColor}15` }}>
                                    <i className="ri-add-line" style={{ color: accentColor }}></i>
                                  </div>
                                  <span>{addon}</span>
                                </div>
                              ))}
                            </div>

                            <div className="airiq-pricing-footer">
                              <span>✓ Transparent pricing, no hidden fees</span>
                              <span className="airiq-support-link" style={{ color: accentColor }}>
                                Need help? Chat with us
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Styles with airiq- prefix */}
      <style jsx>{`
        .airiq-supplier-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          line-height: 1.6;
          color: #1e293b;
        }

        .airiq-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Header Styles */
        .airiq-header {
          background: #ffffff;
          color: #1e293b;
          padding: 80px 0 40px;
          text-align: center;
        }

        .airiq-title {
          font-size: 3.5rem;
          font-weight: 800;
          margin: 0 0 15px;
          line-height: 1.2;
          color: #1e293b;
        }

        .airiq-highlight {
          font-size: 4rem;
          display: inline-block;
          font-weight: 800;
        }

        .airiq-subtitle {
          font-size: 1.25rem;
          max-width: 700px;
          margin: 0 auto;
          color: #475569;
        }

        /* Tabs Navigation */
        .airiq-tabs-container {
          background: #ffffff;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 40px;
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
        }

        .airiq-tabs {
          display: flex;
          gap: 30px;
          overflow-x: auto;
          padding: 0 24px;
        }

        .airiq-tab {
          background: none;
          border: none;
          padding: 16px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .airiq-tab:hover {
          color: #0f172a;
        }

        .airiq-tab-active {
          color: #0f172a;
        }

        .airiq-tab-active::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: ${airiqData.themeColor};
        }

        .airiq-tab-badge {
          background: ${airiqData.themeColor};
          color: white;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* About Section */
        .airiq-about {
          padding: 40px 0 60px;
          background: #ffffff;
        }

        .airiq-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 50px;
          align-items: start;
        }

        .airiq-section-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 24px;
          color: #0f172a;
        }

        .airiq-text-center {
          text-align: center;
        }

        .airiq-accent {
          position: relative;
          display: inline-block;
        }

        .airiq-accent::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 100%;
          height: 4px;
          background: ${airiqData.themeColor};
          border-radius: 2px;
        }

        .airiq-text {
          font-size: 1.1rem;
          color: #475569;
          margin-bottom: 20px;
          line-height: 1.8;
        }

        .airiq-features {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-top: 30px;
        }

        .airiq-feature {
          display: flex;
          gap: 15px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .airiq-feature:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.05);
          border-color: ${airiqData.themeColor};
        }

        .airiq-feature-icon {
          font-size: 2rem;
          min-width: 50px;
        }

        .airiq-feature h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 5px;
          color: #0f172a;
        }

        .airiq-feature p {
          font-size: 0.95rem;
          color: #475569;
          margin: 0;
        }

        .airiq-card {
          background: white;
          padding: 40px;
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
          border: 1px solid #eef2f6;
        }

        .airiq-card-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 24px;
          color: #0f172a;
        }

        .airiq-mt-4 {
          margin-top: 30px;
        }

        .airiq-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .airiq-list-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          font-size: 1rem;
          color: #334155;
        }

        .airiq-check {
          font-weight: bold;
          font-size: 1.2rem;
        }

        .airiq-leadership-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .airiq-leadership-tag {
          background: #f1f5f9;
          color: #334155;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          border: 1px solid #e2e8f0;
        }

        /* Services Section */
        .airiq-services {
          padding: 40px 0 60px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .airiq-section-subtitle {
          font-size: 1.25rem;
          color: #475569;
          text-align: center;
          max-width: 700px;
          margin: 0 auto 50px;
        }

        .airiq-services-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }

        .airiq-service-card {
          background: white;
          padding: 35px 25px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          border: 1px solid #eef2f6;
          position: relative;
          overflow: hidden;
        }

        .airiq-service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: ${airiqData.themeColor};
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .airiq-service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(2, 132, 199, 0.1);
        }

        .airiq-service-card:hover::before {
          transform: scaleX(1);
        }

        .airiq-service-icon {
          font-size: 3rem;
          margin-bottom: 20px;
        }

        .airiq-service-title {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .airiq-service-description {
          color: #475569;
          margin-bottom: 20px;
          line-height: 1.6;
        }

        .airiq-service-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .airiq-service-list-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          color: #475569;
          font-size: 0.95rem;
        }

        .airiq-dot {
          font-weight: bold;
        }

        /* Technology Section */
        .airiq-technology {
          padding: 40px 0 60px;
          background: white;
        }

        .airiq-integration-box,
        .airiq-benefits-box {
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          padding: 40px;
          border-radius: 24px;
          border: 1px solid #eef2f6;
        }

        .airiq-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 25px;
        }

        .airiq-tag {
          background: white;
          padding: 8px 16px;
          border-radius: 30px;
          font-size: 0.9rem;
          color: ${airiqData.themeColor};
          font-weight: 500;
          border: 1px solid ${airiqData.themeColor}30;
          transition: all 0.3s ease;
        }

        .airiq-tag:hover {
          background: ${airiqData.themeColor};
          color: white;
          border-color: ${airiqData.themeColor};
          transform: scale(1.05);
        }

        .airiq-benefits-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .airiq-benefits-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          font-size: 1.1rem;
          color: #1e293b;
        }

        .airiq-benefits-icon {
          font-size: 1.5rem;
          background: white;
          width: 45px;
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.05);
        }

        /* Why Zotrai Section */
        .airiq-why-zotrai-section {
          padding: 40px 0 80px;
          background: #ffffff;
        }

        .airiq-why-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .airiq-why-badge {
          display: inline-block;
          padding: 8px 20px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 20px;
        }

        .airiq-benefits-grid-full {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 25px;
          margin-bottom: 50px;
        }

        .airiq-benefit-card {
          display: flex;
          gap: 20px;
          background: #f8fafc;
          padding: 25px;
          border-radius: 20px;
          border: 1px solid #eef2f6;
          transition: all 0.3s ease;
        }

        .airiq-benefit-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.05);
          border-color: ${companyInfo.themeColor};
        }

        .airiq-benefit-icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .airiq-benefit-icon-wrapper i {
          font-size: 2rem;
        }

        .airiq-benefit-content h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 8px;
          color: #0f172a;
        }

        .airiq-benefit-content p {
          color: #475569;
          font-size: 0.95rem;
          margin-bottom: 12px;
          line-height: 1.6;
        }

        .airiq-benefit-stat {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .airiq-stat-highlight {
          font-size: 1.2rem;
          font-weight: 700;
        }

        .airiq-integration-process {
          text-align: center;
          margin: 60px 0;
        }

        .airiq-process-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 40px;
          color: #0f172a;
        }

        .airiq-process-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }

        .airiq-process-step {
          text-align: center;
          padding: 30px;
          background: #f8fafc;
          border-radius: 20px;
          border: 1px solid #eef2f6;
        }

        .airiq-process-step .airiq-step-number {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 auto 20px;
        }

        /* Contact Section */
        .airiq-contact-section {
          background: #ffffff;
          padding: 60px;
          border-radius: 30px;
          text-align: center;
          color: #0f172a;
          margin-top: 60px;
          border: 2px solid #eef2f6;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }

        .airiq-contact-section h3 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .airiq-contact-section p {
          font-size: 1.2rem;
          color: #475569;
          margin-bottom: 40px;
        }

        .airiq-contact-info {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .airiq-contact-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px 30px;
          background: #f8fafc;
          border-radius: 16px;
          border: 1px solid #eef2f6;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 280px;
        }

        .airiq-contact-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          border-color: ${companyInfo.themeColor};
        }

        .airiq-contact-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .airiq-contact-icon i {
          font-size: 1.8rem;
        }

        .airiq-contact-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .airiq-contact-label {
          font-size: 0.85rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .airiq-contact-value {
          font-size: 1.2rem;
          font-weight: 600;
          color: #0f172a;
        }

        .airiq-contact-note {
          font-size: 0.95rem !important;
          color: #64748b !important;
          margin-top: 20px;
          margin-bottom: 0 !important;
        }

        /* Suppliers Section */
        .airiq-suppliers-section {
          padding: 40px 0 80px;
          background: #ffffff;
        }

        .airiq-suppliers-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .airiq-suppliers-badge {
          display: inline-block;
          background: linear-gradient(135deg, #2563eb15, #7c3aed15);
          color: #2563eb;
          padding: 8px 20px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 20px;
          border: 1px solid #2563eb20;
        }

        .airiq-suppliers-badge i {
          margin-right: 8px;
        }

        .airiq-logo-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          margin-bottom: 50px;
        }

        .airiq-logo-item {
          text-align: center;
          padding: 20px;
          background: white;
          border-radius: 16px;
          border: 1px solid #eef2f6;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .airiq-logo-item:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(37, 99, 235, 0.15);
          border-color: #2563eb;
        }

        .airiq-logo-item:hover .airiq-view-details-hint {
          opacity: 1;
          transform: translateY(0);
        }

        .airiq-logo-wrapper {
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 15px;
        }

        .airiq-supplier-logo {
          max-width: 120px;
          max-height: 60px;
          object-fit: contain;
          transition: transform 0.3s ease;
        }

        .airiq-logo-item:hover .airiq-supplier-logo {
          transform: scale(1.05);
        }

        .airiq-logo-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1e293b;
          display: block;
          margin-bottom: 5px;
        }

        .airiq-view-details-hint {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          color: #2563eb;
          font-weight: 500;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.3s ease;
        }

        .airiq-supplier-stats-simple {
          display: flex;
          justify-content: space-around;
          align-items: center;
          background: linear-gradient(135deg, #f8fafc, #ffffff);
          padding: 30px;
          border-radius: 20px;
          border: 1px solid #eef2f6;
          flex-wrap: wrap;
          gap: 20px;
        }

        .airiq-stat-simple {
          text-align: center;
        }

        .airiq-stat-simple .airiq-stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 800;
          color: #2563eb;
          line-height: 1.2;
        }

        .airiq-stat-simple .airiq-stat-label {
          font-size: 0.9rem;
          color: #64748b;
        }

        /* Pricing Section */
        .airiq-pricing-section {
          padding: 40px 0 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        }

        .airiq-pricing-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .airiq-billing-toggle {
          display: inline-flex;
          background: white;
          padding: 4px;
          border-radius: 50px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          margin-top: 20px;
        }

        .airiq-toggle-btn {
          border: none;
          padding: 12px 32px;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: #64748b;
          position: relative;
        }

        .airiq-toggle-active {
          background: #2563eb;
          color: white;
        }

        .airiq-save-badge {
          background: #fbbf24;
          color: #1e293b;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 8px;
          font-weight: 500;
        }

        .airiq-pricing-grid {
          max-width: 1000px;
          margin: 0 auto;
        }

        .airiq-pricing-card-enhanced {
          background: white;
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
          border: 1px solid #eef2f6;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .airiq-pricing-card-enhanced:hover {
          transform: translateY(-5px);
          box-shadow: 0 30px 60px rgba(37, 99, 235, 0.12);
        }

        .airiq-popular-badge {
          position: absolute;
          top: 20px;
          right: 30px;
          background: #2563eb;
          color: white;
          padding: 8px 16px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.85rem;
          z-index: 10;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
        }

        .airiq-pricing-card-content {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
        }

        .airiq-pricing-left {
          padding: 40px;
          border-right: 1px solid #eef2f6;
        }

        .airiq-pricing-emoji {
          font-size: 2.5rem;
          margin-bottom: 15px;
        }

        .airiq-pricing-name {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: #0f172a;
        }

        .airiq-pricing-description {
          color: #475569;
          font-size: 0.95rem;
          margin-bottom: 25px;
        }

        .airiq-pricing-amount {
          margin-bottom: 25px;
        }

        .airiq-currency {
          font-size: 1.2rem;
          font-weight: 600;
          color: #64748b;
          margin-right: 5px;
        }

        .airiq-price {
          font-size: 3.5rem;
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
        }

        .airiq-pricing-period {
          font-size: 1rem;
          color: #64748b;
          margin-left: 5px;
        }

        .airiq-pricing-cta {
          width: 100%;
          border: none;
          padding: 16px;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 30px;
        }

        .airiq-pricing-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }

        .airiq-quick-setup {
          background: #f8fafc;
          border-radius: 16px;
          padding: 20px;
        }

        .airiq-setup-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 20px;
          color: #0f172a;
        }

        .airiq-setup-steps {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .airiq-setup-step {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .airiq-step-marker {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          flex-shrink: 0;
        }

        .airiq-step-content {
          display: flex;
          flex-direction: column;
        }

        .airiq-step-content strong {
          font-size: 0.95rem;
          margin-bottom: 4px;
        }

        .airiq-step-content span {
          font-size: 0.85rem;
          color: #64748b;
        }

        .airiq-pricing-right {
          padding: 40px;
          background: #ffffff;
        }

        .airiq-features-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .airiq-addons-title {
          margin-top: 30px;
        }

        .airiq-features-list,
        .airiq-addons-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .airiq-feature-item,
        .airiq-addon-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .airiq-feature-check,
        .airiq-addon-check {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .airiq-feature-check i,
        .airiq-addon-check i {
          font-size: 14px;
        }

        .airiq-feature-item span,
        .airiq-addon-item span {
          font-size: 0.95rem;
          color: #334155;
          line-height: 1.5;
        }

        .airiq-pricing-footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
        }

        .airiq-support-link {
          cursor: pointer;
          font-weight: 500;
        }

        .airiq-support-link:hover {
          text-decoration: underline;
        }

        /* Accessibility */
        .airiq-clickable:focus-visible {
          outline: 3px solid #2563eb;
          outline-offset: 2px;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .airiq-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }
          
          .airiq-services-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .airiq-benefits-grid-full {
            grid-template-columns: 1fr;
          }

          .airiq-process-steps {
            grid-template-columns: 1fr;
          }

          .airiq-logo-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .airiq-pricing-card-content {
            grid-template-columns: 1fr;
          }

          .airiq-pricing-left {
            border-right: none;
            border-bottom: 1px solid #eef2f6;
          }

          .airiq-contact-info {
            flex-direction: column;
            align-items: center;
          }

          .airiq-contact-item {
            width: 100%;
            max-width: 400px;
          }
        }

        @media (max-width: 768px) {
          .airiq-title {
            font-size: 2.5rem;
          }
          
          .airiq-highlight {
            font-size: 3rem;
          }
          
          .airiq-tabs {
            gap: 20px;
            overflow-x: auto;
            padding-bottom: 5px;
          }
          
          .airiq-tab {
            font-size: 0.95rem;
            white-space: nowrap;
          }
          
          .airiq-services-grid {
            grid-template-columns: 1fr;
          }
          
          .airiq-section-title {
            font-size: 2rem;
          }

          .airiq-features {
            grid-template-columns: 1fr;
          }

          .airiq-logo-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .airiq-supplier-stats-simple {
            flex-direction: column;
            gap: 20px;
          }

          .airiq-contact-section {
            padding: 40px 20px;
          }

          .airiq-contact-section h3 {
            font-size: 1.8rem;
          }

          .airiq-contact-item {
            padding: 15px 20px;
          }

          .airiq-contact-value {
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .airiq-header {
            padding: 60px 0 30px;
          }

          .airiq-logo-grid {
            grid-template-columns: 1fr;
          }

          .airiq-pricing-left,
          .airiq-pricing-right {
            padding: 25px;
          }

          .airiq-price {
            font-size: 2.5rem;
          }

          .airiq-billing-toggle {
            flex-direction: column;
            width: 100%;
            border-radius: 12px;
          }

          .airiq-toggle-btn {
            width: 100%;
          }

          .airiq-contact-item {
            flex-direction: column;
            text-align: center;
          }

          .airiq-contact-details {
            align-items: center;
          }
        }
      `}</style>
    </>
  );
};

// Simple Helmet component for meta tags
const Helmet = ({ children }) => {
  return <>{children}</>;
};

export default AirIQSupplierHighlight;